/**
 * Animated background with gradient orbs and particle effects
 * Gaming/tech aesthetic for PhoenixBilling
 */

export function ParticleBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      {/* Base gradient mesh background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

      {/* Animated gradient mesh overlay */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-50" />

      {/* Animated gradient orbs */}
      <div
        className="absolute top-0 -left-4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-float"
        style={{ animationDelay: '0s' }}
      />
      <div
        className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float"
        style={{ animationDelay: '1s' }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="absolute top-1/4 right-1/4 w-64 h-64 bg-green-500/10 rounded-full blur-3xl animate-float"
        style={{ animationDelay: '1.5s' }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Vignette effect */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at center, transparent 0%, rgba(15, 23, 42, 0.5) 100%)',
        }}
      />
    </div>
  );
}

/**
 * Simpler background for pages that need less distraction
 */
export function SimpleBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
      <div className="absolute inset-0 bg-gradient-mesh opacity-30" />
    </div>
  );
}

/**
 * Glowing border effect for cards/modals
 */
interface GlowingBorderProps {
  children: React.ReactNode;
  color?: 'orange' | 'purple' | 'cyan' | 'green';
  intensity?: 'low' | 'medium' | 'high';
  animated?: boolean;
}

export function GlowingBorder({
  children,
  color = 'orange',
  intensity = 'medium',
  animated = true,
}: GlowingBorderProps) {
  const colorClasses = {
    orange: 'from-orange-500 via-orange-600 to-orange-500',
    purple: 'from-purple-500 via-purple-600 to-purple-500',
    cyan: 'from-cyan-500 via-cyan-600 to-cyan-500',
    green: 'from-green-500 via-green-600 to-green-500',
  };

  const intensityOpacity = {
    low: 'opacity-30',
    medium: 'opacity-50',
    high: 'opacity-70',
  };

  return (
    <div className="relative p-[1px] rounded-xl overflow-hidden">
      {/* Animated gradient border */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${colorClasses[color]} ${
          intensityOpacity[intensity]
        } ${animated ? 'animate-gradient-border' : ''}`}
        style={{
          backgroundSize: animated ? '200% 200%' : '100% 100%',
        }}
      />

      {/* Content */}
      <div className="relative bg-slate-800 rounded-xl">{children}</div>
    </div>
  );
}
